/**
 * Navigation State Manager
 * Manages view state and persistence for Kubernetes cluster pages
 */

export interface ClusterView {
  type: 'list' | 'details' | 'upgrade-wizard' | 'upgrade-progress';
  clusterId?: string;
  clusterName?: string;
  data?: any;
}

export interface NavigationState {
  currentView: ClusterView;
  history: ClusterView[];
  upgradeState?: {
    clusterId: string;
    clusterName: string;
    targetVersion: string;
    startedAt: string;
    status: 'in_progress' | 'completed' | 'failed';
    estimatedTime: number;
  };
}

const STORAGE_KEY = 'devopsx_navigation_state';
const UPGRADE_STATE_KEY = 'devopsx_upgrade_state';

class NavigationStateManager {
  private state: NavigationState = {
    currentView: { type: 'list' },
    history: []
  };

  constructor() {
    this.loadState();
  }

  /**
   * Load state from localStorage
   */
  private loadState() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        this.state = JSON.parse(stored);
      }

      // Load upgrade state separately
      const upgradeState = localStorage.getItem(UPGRADE_STATE_KEY);
      if (upgradeState) {
        this.state.upgradeState = JSON.parse(upgradeState);
      }
    } catch (error) {
      console.error('Failed to load navigation state:', error);
    }
  }

  /**
   * Save state to localStorage
   */
  private saveState() {
    try {
      const { upgradeState, ...navState } = this.state;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(navState));

      if (upgradeState) {
        localStorage.setItem(UPGRADE_STATE_KEY, JSON.stringify(upgradeState));
      }
    } catch (error) {
      console.error('Failed to save navigation state:', error);
    }
  }

  /**
   * Navigate to a new view
   */
  navigateTo(view: ClusterView) {
    this.state.history.push(this.state.currentView);
    this.state.currentView = view;
    this.saveState();
  }

  /**
   * Go back to previous view
   */
  goBack() {
    if (this.state.history.length > 0) {
      this.state.currentView = this.state.history.pop()!;
      this.saveState();
      return true;
    }
    return false;
  }

  /**
   * Navigate to cluster list
   */
  goToList() {
    this.state.currentView = { type: 'list' };
    this.state.history = [];
    this.saveState();
  }

  /**
   * Navigate to cluster details
   */
  goToClusterDetails(clusterId: string, clusterName: string, data?: any) {
    this.navigateTo({
      type: 'details',
      clusterId,
      clusterName,
      data
    });
  }

  /**
   * Navigate to upgrade wizard
   */
  goToUpgradeWizard(clusterId: string, clusterName: string, data?: any) {
    this.navigateTo({
      type: 'upgrade-wizard',
      clusterId,
      clusterName,
      data
    });
  }

  /**
   * Navigate to upgrade progress
   */
  goToUpgradeProgress(clusterId: string, clusterName: string, targetVersion: string, estimatedTime: number) {
    this.navigateTo({
      type: 'upgrade-progress',
      clusterId,
      clusterName
    });

    // Store upgrade state
    this.state.upgradeState = {
      clusterId,
      clusterName,
      targetVersion,
      startedAt: new Date().toISOString(),
      status: 'in_progress',
      estimatedTime
    };
    this.saveState();
  }

  /**
   * Update upgrade state
   */
  updateUpgradeState(status: 'in_progress' | 'completed' | 'failed') {
    if (this.state.upgradeState) {
      this.state.upgradeState.status = status;
      this.saveState();
    }
  }

  /**
   * Clear upgrade state
   */
  clearUpgradeState() {
    this.state.upgradeState = undefined;
    localStorage.removeItem(UPGRADE_STATE_KEY);
    this.saveState();
  }

  /**
   * Get current view
   */
  getCurrentView(): ClusterView {
    return this.state.currentView;
  }

  /**
   * Get upgrade state
   */
  getUpgradeState() {
    return this.state.upgradeState;
  }

  /**
   * Check if there's an active upgrade
   */
  hasActiveUpgrade(): boolean {
    return this.state.upgradeState?.status === 'in_progress';
  }

  /**
   * Get breadcrumb path
   */
  getBreadcrumbs(): Array<{ label: string; onClick?: () => void }> {
    const breadcrumbs: Array<{ label: string; onClick?: () => void }> = [
      { label: 'EKS Clusters', onClick: () => this.goToList() }
    ];

    const view = this.state.currentView;

    if (view.type !== 'list') {
      breadcrumbs.push({
        label: view.clusterName || 'Cluster',
        onClick: view.clusterId ? () => this.goToClusterDetails(view.clusterId!, view.clusterName!) : undefined
      });
    }

    if (view.type === 'upgrade-wizard') {
      breadcrumbs.push({ label: 'Upgrade Analysis' });
    } else if (view.type === 'upgrade-progress') {
      breadcrumbs.push({ label: 'Upgrade Progress' });
    }

    return breadcrumbs;
  }

  /**
   * Reset to default state
   */
  reset() {
    this.state = {
      currentView: { type: 'list' },
      history: []
    };
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(UPGRADE_STATE_KEY);
  }
}

export const navigationState = new NavigationStateManager();
